import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DocumentiPicker, uploadDocumenti } from "./DocumentiPicker";
import api, { apiError } from "../lib/api";
import { apriPortaleDopoSalvataggio } from "./PortaleBox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import { LAVORAZIONI, ENEL_OPERAZIONI } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

const EMPTY = {
  nome: "", cognome: "", tipo_cliente: "privato", codice_fiscale: "", p_iva: "", telefoni_extra: "", email_extra: "", uso_utenza: "",
  indirizzo: "", civico: "", cap: "", comune: "", provincia: "", pod: "", pdr: "", iban: "", email: "", telefono: "",
  kw_potenza: "", tipo_bolletta: "luce", fornitore_provenienza: "", gestione: "cambiaora", enel_operazione: "",
  costo_kwh_attuale: "", spese_fisse_attuale: "", costo_smc_attuale: "",
  data_contratto: "", data_verifica: "", data_cambio: "", tipo_contratto: "fisso",
  nuovo_fornitore: "", costo_kwh_nuovo: "", spese_fisse_nuovo: "", costo_smc_nuovo: "",
  privacy_firmata: false, note: "", data_nascita: "", lavorazione: "da_quotare", venditore_id: "", operatore_id: "",
};

const NUM_FIELDS = ["kw_potenza", "costo_kwh_attuale", "spese_fisse_attuale", "costo_smc_attuale",
  "costo_kwh_nuovo", "spese_fisse_nuovo", "costo_smc_nuovo"];
const DATE_FIELDS = ["data_contratto", "data_verifica", "data_cambio"];

function FornitoreSelect({ campo, value, set, suppliers, nuovo, setNuovo, onAdd, testid }) {
  if (nuovo?.campo === campo) {
    return (
      <div className="flex gap-2">
        <Input autoFocus placeholder="Nome nuovo fornitore" value={nuovo.value}
               onChange={(e) => setNuovo({ campo, value: e.target.value })}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }} data-testid={`${testid}-nuovo-input`} />
        <Button type="button" size="sm" onClick={onAdd} data-testid={`${testid}-nuovo-salva`}>Aggiungi</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setNuovo(null)} data-testid={`${testid}-nuovo-annulla`}>✕</Button>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <Select value={value} onValueChange={(v) => set(campo, v)}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Seleziona fornitore" /></SelectTrigger>
        <SelectContent className="max-h-64">
          {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button type="button" size="sm" variant="outline" title="Aggiungi un fornitore non in elenco"
              onClick={() => setNuovo({ campo, value: "" })} data-testid={`${testid}-nuovo-btn`}>+ Nuovo</Button>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid ? `field-${testid}` : undefined}>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

const PROSSIMI = [
  { id: "luce", label: "Utenza Luce" }, { id: "gas", label: "Utenza Gas" }, { id: "sim", label: "SIM" }, { id: "internet", label: "Internet" },
  { id: "fisso", label: "Telefono fisso" }, { id: "riparazione", label: "Riparazione" }, { id: "accessori", label: "Accessori" }, { id: "vendita", label: "Vendita" }, { id: "ritiro", label: "Ritiro telefono" },
];

export default function ClientForm({ open, onClose, client, meta, onSaved, prefill, onAggiungi }) {
  const { user } = useAuth();
  const [prossimo, setProssimo] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(EMPTY);
  const [venditori, setVenditori] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [nuovoFornitore, setNuovoFornitore] = useState(null);
  const [nuoviDoc, setNuoviDoc] = useState([]);

  useEffect(() => {
    api.get("/venditori").then((r) => setVenditori(r.data.filter((v) => v.attivo !== false))).catch(() => {});
  }, []);
  useEffect(() => { setSuppliers(meta.suppliers || []); }, [meta.suppliers]);

  const aggiungiFornitore = async () => {
    const nome = (nuovoFornitore?.value || "").trim();
    if (nome.length < 2) return;
    try {
      const r = await api.post("/fornitori", { nome });
      setSuppliers((s) => (s.includes(r.data.nome) ? s : [...s, r.data.nome]));
      set(nuovoFornitore.campo, r.data.nome);
      setNuovoFornitore(null);
      toast.success(`Fornitore "${r.data.nome}" aggiunto`);
    } catch (e) {
      toast.error(apiError(e, "Impossibile aggiungere il fornitore"));
    }
  };
  const isEdit = Boolean(client);
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const draftKey = `client_form_draft:${client?.id || "nuovo"}`;
  const [draft, setDraft] = useState(null);

  // Bozza automatica: salva in locale ogni modifica (solo se ci sono dati diversi dalla base)
  useEffect(() => {
    if (!open || draft || !dirty) return;
    localStorage.setItem(draftKey, JSON.stringify({ form, at: new Date().toISOString() }));
  }, [form, dirty, open, draftKey, draft]);

  const ripristinaBozza = () => {
    if (draft?.form) setForm({ ...EMPTY, ...draft.form });
    setDraft(null);
    toast.success("Bozza ripristinata");
  };
  const scartaBozza = () => {
    localStorage.removeItem(draftKey);
    setDraft(null);
  };

  useEffect(() => {
    const handler = (e) => {
      if (open && dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [open, dirty]);

  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(`client_form_draft:${client?.id || "nuovo"}`);
      setDraft(raw ? JSON.parse(raw) : null);
    } catch {
      setDraft(null);
    }
    const applica = (src) => {
      const f = { ...EMPTY, ...src };
      NUM_FIELDS.forEach((k) => { f[k] = f[k] ?? ""; });
      DATE_FIELDS.forEach((k) => { f[k] = f[k] ? f[k].slice(0, 10) : ""; });
      setForm(f);
      setBaseline(f);
    };
    if (client) {
      applica(client);
      // La riga della lista ha solo alcuni campi: carico SEMPRE la scheda completa per non sovrascrivere dati
      api.get(`/clients/${client.id}`).then((r) => applica(r.data)).catch(() => toast.error("Impossibile caricare la scheda completa"));
    } else {
      const f = { ...EMPTY, venditore_id: meta.stores[0]?.id || "", data_contratto: new Date().toISOString().slice(0, 10), ...(prefill || {}) };
      setForm(f);
      setBaseline(prefill ? { ...EMPTY, venditore_id: meta.stores[0]?.id || "" } : f);
    }
  }, [open, client, meta, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isLuce = form.tipo_bolletta === "luce";
  const tipoCliente = form.tipo_cliente === "business" ? "societa" : (form.tipo_cliente || "privato");
  const isBusiness = tipoCliente !== "privato";
  const isSocieta = tipoCliente === "societa";

  const handleClose = () => {
    if (dirty && !isEdit) {
      if (!window.confirm("Ci sono dati non salvati. Vuoi uscire senza salvare? (La bozza resta salvata in locale)")) return;
    }
    onClose();
  };

  const isCambiaOraNuova = !isEdit && form.gestione !== "enel" && !prefill?._nuova_utenza;
  const isRinnovoCambiaOra = isEdit && form.gestione !== "enel" && !!client?.lavorazione;
  const haBolletta = nuoviDoc.some((f) => f.tipo === "bolletta");
  const haDocumento = nuoviDoc.some((f) => f.tipo === "documento");
  const submit = async (e) => {
    e.preventDefault();
    if (isCambiaOraNuova && (!haBolletta || !haDocumento)) {
      const manca = [!haBolletta && "la bolletta", !haDocumento && "il documento d'identità"].filter(Boolean).join(" e ");
      if (user?.role !== "admin") { toast.error(`Pratica CambiaOra non salvata: manca ${manca}. Fotografali o allegali qui sotto.`); return; }
      if (!window.confirm(`Manca ${manca}. Solo l'amministratore può salvare comunque: confermi?`)) return;
    }
    setSaving(true);
    const { _nuova_utenza, ...formPulito } = form;
    const splitList = (v) => (Array.isArray(v) ? v : String(v || "").split(/[,;\n]/)).map((x) => x.trim()).filter(Boolean);
    const payload = { ...formPulito, tipo_cliente: tipoCliente, telefoni_extra: splitList(form.telefoni_extra), email_extra: splitList(form.email_extra) };
    NUM_FIELDS.forEach((k) => { payload[k] = payload[k] === "" ? null : parseFloat(payload[k]); });
    DATE_FIELDS.forEach((k) => { payload[k] = payload[k] || null; });
    try {
      let clientId = client?.id;
      let salvato = null;
      if (isEdit) {
        const r = await api.patch(`/clients/${client.id}`, payload);
        salvato = r.data;
        toast.success("Cliente aggiornato");
      } else {
        const r = await api.post("/clients", payload);
        clientId = r.data.id;
        salvato = r.data;
        toast.success("Cliente inserito");
        if (payload.nuovo_fornitore) api.get("/portali").then((r) => apriPortaleDopoSalvataggio(r.data, "energia", payload.nuovo_fornitore)).catch(() => {});
      }
      if (nuoviDoc.length && clientId) {
        try {
          await uploadDocumenti(nuoviDoc, { clientId });
          toast.success(`${nuoviDoc.length} documenti allegati al cliente`);
        } catch (e) { toast.error(apiError(e, "Cliente salvato ma documenti non caricati: allegali dalla scheda")); }
      }
      setNuoviDoc([]);
      setForm(EMPTY);
      setBaseline(EMPTY);
      localStorage.removeItem(draftKey);
      onSaved();
      onClose();
      if (!isEdit && prossimo && salvato) onAggiungi?.(prossimo, salvato);
      setProssimo("");
    } catch (err) {
      toast.error(apiError(err, "Salvataggio fallito"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? `Modifica ${client.cognome} ${client.nome}` : prefill?._nuova_utenza ? `Nuova utenza ${prefill.tipo_bolletta === "gas" ? "Gas" : "Luce"} · ${prefill.cognome} ${prefill.nome}` : "Nuovo cliente"}
          </DialogTitle>
          {!isEdit && prefill?._nuova_utenza && (
            <p className="text-sm text-slate-600" data-testid="nuova-utenza-hint">Anagrafica copiata dal cliente. Se l'utenza è a un altro indirizzo (seconda casa, negozio, ecc.) cambia Via / Comune qui sotto: ogni utenza ha il suo indirizzo e il suo compenso.</p>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6" data-testid="client-form">
          {draft && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="client-draft-banner">
              <span>
                Bozza non salvata trovata ({new Date(draft.at).toLocaleString("it-IT")})
                {draft.form?.cognome || draft.form?.nome ? ` — ${draft.form.cognome || ""} ${draft.form.nome || ""}`.trimEnd() : ""}
              </span>
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={ripristinaBozza} data-testid="client-draft-restore" className="bg-amber-600 hover:bg-amber-700">Ripristina bozza</Button>
                <Button type="button" size="sm" variant="outline" onClick={scartaBozza} data-testid="client-draft-discard">Scarta</Button>
              </div>
            </div>
          )}
          <section>
            <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Anagrafica</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Nome *" testid="nome">
                <Input required value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="input-nome" />
              </Field>
              <Field label={isSocieta ? "Ragione sociale *" : "Cognome *"} testid="cognome">
                <Input required value={form.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="input-cognome" />
              </Field>
              <Field label="Tipo cliente" testid="tipo-cliente">
                <Select value={tipoCliente} onValueChange={(v) => set("tipo_cliente", v)}>
                  <SelectTrigger data-testid="select-tipo-cliente"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="privato">Privato (solo CF)</SelectItem>
                    <SelectItem value="ditta_individuale">Ditta individuale (CF + P.IVA)</SelectItem>
                    <SelectItem value="societa">Società (P.IVA)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={tipoCliente === "ditta_individuale" ? "Codice Fiscale *" : "Codice Fiscale"} testid="cf">
                <Input required={tipoCliente === "ditta_individuale"} value={form.codice_fiscale}
                       onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} data-testid="input-codice-fiscale" />
              </Field>
              {isBusiness && (
                <Field label="Partita IVA *" testid="piva">
                  <Input required value={form.p_iva} onChange={(e) => set("p_iva", e.target.value.replace(/\D/g, "").slice(0, 11))}
                         placeholder="11 cifre" data-testid="input-p-iva" />
                </Field>
              )}
              <Field label={prefill?._nuova_utenza ? "Via / Indirizzo dell'utenza" : "Via / Indirizzo"} testid="indirizzo">
                <Input value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} data-testid="input-indirizzo" className={prefill?._nuova_utenza ? "border-violet-300 bg-violet-50/40" : ""} />
              </Field>
              <Field label="Uso utenza (indirizzo)" testid="uso-utenza">
                <div className="flex gap-1.5" data-testid="uso-utenza-chips">
                  {[["residenza", "Residenza"], ["non_residenza", "Non residenza"], ["business", "Business"]].map(([id, l]) => (
                    <button type="button" key={id} onClick={() => set("uso_utenza", form.uso_utenza === id ? "" : id)} data-testid={`uso-utenza-${id}`}
                            className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${form.uso_utenza === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>{l}</button>
                  ))}
                </div>
              </Field>
              <Field label="N. civico" testid="civico">
                <Input value={form.civico || ""} onChange={(e) => set("civico", e.target.value)} data-testid="input-civico" />
              </Field>
              <Field label="CAP" testid="cap">
                <Input inputMode="numeric" maxLength={5} value={form.cap || ""} onChange={(e) => set("cap", e.target.value.replace(/\D/g, ""))} data-testid="input-cap" />
              </Field>
              <Field label="Comune" testid="comune">
                <Input value={form.comune || ""} onChange={(e) => set("comune", e.target.value)} data-testid="input-comune" />
              </Field>
              <Field label="Provincia" testid="provincia">
                <Input placeholder="es. SO" maxLength={2} value={form.provincia || ""} onChange={(e) => set("provincia", e.target.value.toUpperCase())} data-testid="input-provincia" />
              </Field>
              <Field label="Email" testid="email">
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" />
              </Field>
              <Field label="Telefono" testid="telefono">
                <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} data-testid="input-telefono" />
              </Field>
              <Field label="Altri telefoni (separati da virgola)" testid="telefoni-extra">
                <Input placeholder="es. 333 1234567, 0342 123456" value={Array.isArray(form.telefoni_extra) ? form.telefoni_extra.join(", ") : form.telefoni_extra || ""} onChange={(e) => set("telefoni_extra", e.target.value)} data-testid="input-telefoni-extra" />
              </Field>
              <Field label="Altre email (separate da virgola)" testid="email-extra">
                <Input placeholder="es. lavoro@azienda.it" value={Array.isArray(form.email_extra) ? form.email_extra.join(", ") : form.email_extra || ""} onChange={(e) => set("email_extra", e.target.value)} data-testid="input-email-extra" />
              </Field>
              <Field label="IBAN" testid="iban">
                <Input value={form.iban} onChange={(e) => set("iban", e.target.value.toUpperCase())} data-testid="input-iban" />
              </Field>
            </div>
          </section>

          <section>
            <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Utenza attuale</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label={isEdit && client?.gestione === "enel" ? "Gestione (ENEL — bloccata)" : "Gestione"} testid="gestione">
                <Select value={form.gestione || "cambiaora"} onValueChange={(v) => set("gestione", v)} disabled={isEdit && form.gestione === "enel" && baseline.gestione === "enel"}>
                  <SelectTrigger data-testid="select-gestione"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cambiaora">CambiaOra (progetto energia, num. 3519460591)</SelectItem>
                    <SelectItem value="enel">ENEL (ufficio utenze dirette Enel)</SelectItem>
                  </SelectContent>
                </Select>
                {isEdit && baseline.gestione === "enel" && (
                  <p className="text-xs text-amber-700">Cliente ENEL: si sposta solo con il tasto "Sposta a CambiaOra" nella scheda.</p>
                )}
              </Field>
              {form.gestione === "enel" && (
                <Field label="Operazione ENEL *" testid="enel-operazione">
                  <Select value={form.enel_operazione || ""} onValueChange={(v) => set("enel_operazione", v)} required>
                    <SelectTrigger data-testid="select-enel-operazione"><SelectValue placeholder="Seleziona operazione" /></SelectTrigger>
                    <SelectContent>
                      {ENEL_OPERAZIONI.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Tipo bolletta" testid="tipo-bolletta">
                <Select value={form.tipo_bolletta} onValueChange={(v) => set("tipo_bolletta", v)}>
                  <SelectTrigger data-testid="select-tipo-bolletta"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="luce">Luce</SelectItem>
                    <SelectItem value="gas">Gas</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {isLuce ? (
                <>
                  <Field label="POD" testid="pod">
                    <Input value={form.pod} onChange={(e) => set("pod", e.target.value.toUpperCase())} data-testid="input-pod" />
                  </Field>
                  <Field label="Potenza (kW)" testid="kw">
                    <Input type="number" step="0.5" value={form.kw_potenza} onChange={(e) => set("kw_potenza", e.target.value)} data-testid="input-kw" />
                  </Field>
                  <Field label="Costo attuale €/kWh" testid="costo-kwh">
                    <Input type="number" step="0.001" value={form.costo_kwh_attuale} onChange={(e) => set("costo_kwh_attuale", e.target.value)} data-testid="input-costo-kwh" />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="PDR" testid="pdr">
                    <Input value={form.pdr} onChange={(e) => set("pdr", e.target.value)} data-testid="input-pdr" />
                  </Field>
                  <Field label="Costo attuale €/Smc" testid="costo-smc">
                    <Input type="number" step="0.001" value={form.costo_smc_attuale} onChange={(e) => set("costo_smc_attuale", e.target.value)} data-testid="input-costo-smc" />
                  </Field>
                </>
              )}
              <Field label="Spese fisse attuali €/mese" testid="spese-fisse">
                <Input type="number" step="0.5" value={form.spese_fisse_attuale} onChange={(e) => set("spese_fisse_attuale", e.target.value)} data-testid="input-spese-fisse" />
              </Field>
              <Field label="Fornitore di provenienza" testid="fornitore">
                <FornitoreSelect campo="fornitore_provenienza" value={form.fornitore_provenienza} set={set} suppliers={suppliers}
                                 nuovo={nuovoFornitore} setNuovo={setNuovoFornitore} onAdd={aggiungiFornitore} testid="select-fornitore" />
              </Field>
            </div>
          </section>

          <section>
            <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Date & nuovo contratto</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Data di contratto" testid="data-contratto">
                <Input type="date" value={form.data_contratto} onChange={(e) => set("data_contratto", e.target.value)} data-testid="input-data-contratto" />
              </Field>
              <Field label="Data di verifica" testid="data-verifica">
                <Input type="date" value={form.data_verifica} onChange={(e) => set("data_verifica", e.target.value)} data-testid="input-data-verifica" />
              </Field>
              <Field label="Data di cambio" testid="data-cambio">
                <Input type="date" value={form.data_cambio} onChange={(e) => set("data_cambio", e.target.value)} data-testid="input-data-cambio" />
              </Field>
              <Field label="Tipo contratto proposto" testid="tipo-contratto">
                <Select value={form.tipo_contratto} onValueChange={(v) => set("tipo_contratto", v)}>
                  <SelectTrigger data-testid="select-tipo-contratto"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fisso">Fisso</SelectItem>
                    <SelectItem value="variabile">Variabile</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Nuovo fornitore" testid="nuovo-fornitore">
                <FornitoreSelect campo="nuovo_fornitore" value={form.nuovo_fornitore} set={set} suppliers={suppliers}
                                 nuovo={nuovoFornitore} setNuovo={setNuovoFornitore} onAdd={aggiungiFornitore} testid="select-nuovo-fornitore" />
              </Field>
              {isLuce ? (
                <Field label="Nuovo costo €/kWh" testid="costo-kwh-nuovo">
                  <Input type="number" step="0.001" value={form.costo_kwh_nuovo} onChange={(e) => set("costo_kwh_nuovo", e.target.value)} data-testid="input-costo-kwh-nuovo" />
                </Field>
              ) : (
                <Field label="Nuovo costo €/Smc" testid="costo-smc-nuovo">
                  <Input type="number" step="0.001" value={form.costo_smc_nuovo} onChange={(e) => set("costo_smc_nuovo", e.target.value)} data-testid="input-costo-smc-nuovo" />
                </Field>
              )}
              <Field label="Nuove spese fisse €/mese" testid="spese-fisse-nuovo">
                <Input type="number" step="0.5" value={form.spese_fisse_nuovo} onChange={(e) => set("spese_fisse_nuovo", e.target.value)} data-testid="input-spese-fisse-nuovo" />
              </Field>
            </div>
          </section>

          <section>
            <h3 className="mb-3 font-heading text-sm font-semibold text-slate-800">Gestione</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Lavorazione" testid="lavorazione">
                <Select value={form.lavorazione} onValueChange={(v) => set("lavorazione", v)}>
                  <SelectTrigger data-testid="select-lavorazione"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {LAVORAZIONI.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Negozio" testid="venditore">
                <Select value={form.venditore_id} onValueChange={(v) => set("venditore_id", v)}>
                  <SelectTrigger data-testid="select-venditore"><SelectValue placeholder="Seleziona negozio" /></SelectTrigger>
                  <SelectContent>
                    {meta.stores.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Venduto da (a chi va il compenso)" testid="operatore">
                <Select value={form.operatore_id || "__negozio__"} onValueChange={(v) => set("operatore_id", v === "__negozio__" ? "" : v)}>
                  <SelectTrigger data-testid="select-operatore"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__negozio__">Negozio (compenso al negozio)</SelectItem>
                    {venditori.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.privacy_firmata} onCheckedChange={(v) => set("privacy_firmata", v)} data-testid="switch-privacy" />
                <Label className="text-sm">Privacy firmata</Label>
              </div>
            </div>
            <div className="mt-4">
              <Field label="Note" testid="note">
                <Textarea rows={3} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="input-note" />
              </Field>
              <Field label="Data di nascita (per gli auguri; se vuota si usa il codice fiscale)" testid="data-nascita">
                <Input type="date" value={form.data_nascita || ""} onChange={(e) => set("data_nascita", e.target.value)} data-testid="input-data-nascita" />
              </Field>
            </div>
            <div className="mt-4">
              {form.gestione !== "enel" && isEdit && isRinnovoCambiaOra && !haBolletta && (
                <div className="mb-2 rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="rinnovo-doc-hint">
                  <b>Consiglio: carica le bollette più recenti.</b> Ai rinnovi e aggiornamenti della pratica Switcho chiede l'ultima bolletta: allegala qui se il cliente l'ha portata (non è obbligatorio per salvare).
                </div>
              )}
              {form.gestione !== "enel" && !isEdit && (
                <div className={`mb-2 rounded-lg border px-3 py-2 text-xs ${isCambiaOraNuova && haBolletta && haDocumento ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="cambiaora-doc-hint">
                  <b>Pratica CambiaOra: bolletta e documento d'identità sono obbligatori per salvare.</b> Switcho li chiede quasi sempre: se sono già qui li inviamo da soli e la pratica non si ferma.
                  {isCambiaOraNuova && <span className="ml-1 font-semibold" data-testid="cambiaora-doc-stato">{haBolletta ? "✓ bolletta" : "✗ bolletta"} · {haDocumento ? "✓ documento" : "✗ documento"}</span>}
                </div>
              )}
              <DocumentiPicker files={nuoviDoc} onChange={setNuoviDoc} testid="client-form-documenti" label="Allega documenti (carta d'identità, bollette, ecc.)" conTipo />
            </div>
          </section>

          {!isEdit && onAggiungi && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4" data-testid="client-form-prossimo">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Dopo il salvataggio aggiungi subito…</p>
              <p className="mb-2 text-[11px] text-slate-500">Ogni cliente può avere utenze, SIM, riparazioni, vendite e ritiri illimitati: scegli cosa aprire appena salvato.</p>
              <div className="flex flex-wrap gap-2">
                {PROSSIMI.map((p) => (
                  <button type="button" key={p.id} onClick={() => setProssimo(prossimo === p.id ? "" : p.id)} data-testid={`client-form-prossimo-${p.id}`}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${prossimo === p.id ? "border-sky-700 bg-sky-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} data-testid="client-form-cancel">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="client-form-submit">
              {saving ? "Salvataggio..." : isEdit ? "Salva modifiche" : prossimo ? `Inserisci e aggiungi ${PROSSIMI.find((p) => p.id === prossimo)?.label}` : "Inserisci cliente"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
